/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Longform Article 4-Step Wizard Container (Task 10.2.3, 10.2.4 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useLongformWizardStore } from '../../stores/useLongformWizardStore';
import { useI18n } from '../../stores/useI18nStore';
import { StepIndicator } from './StepIndicator';
import { Step1SetupView } from './Step1SetupView';
import { Step2TitlesView } from './Step2TitlesView';
import { Step3OutlineView } from './Step3OutlineView';
import { Step4GenerationView } from './Step4GenerationView';
import {
  Wand2,
  RotateCcw,
  FileText,
} from 'lucide-react';

interface LongformArticleWizardProps {
  onNavigateToEditor?: () => void;
}

export const LongformArticleWizard: React.FC<LongformArticleWizardProps> = ({
  onNavigateToEditor,
}) => {
  const {
    currentStep,
    completedSteps,
    setStep,
    resetWizard,
  } = useLongformWizardStore();
  const { t } = useI18n();

  const handleReset = () => {
    if (window.confirm(t('wizard.resetConfirm'))) {
      resetWizard();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* 1. Wizard Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-surface-border bg-surface shadow-glass">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center flex-shrink-0 shadow-glow-primary/30">
            <Wand2 className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading font-extrabold text-xl text-foreground">
                {t('wizard.title')}
              </h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent/15 text-accent border border-accent/20">
                {t('wizard.aiPipeline')}
              </span>
            </div>
            <p className="text-xs text-foreground-muted mt-0.5">
              {t('wizard.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onNavigateToEditor && (
            <button
              type="button"
              onClick={onNavigateToEditor}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-semibold hover:bg-muted text-foreground transition-colors"
            >
              <FileText className="w-3.5 h-3.5 text-primary" />
              <span>{t('wizard.editorBtn')}</span>
            </button>
          )}

          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-semibold hover:bg-muted text-foreground-muted hover:text-foreground transition-colors"
            title={t('wizard.reset')}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{t('wizard.reset')}</span>
          </button>
        </div>
      </div>

      {/* 2. Visual Stepper Progress Bar */}
      <StepIndicator
        currentStep={currentStep}
        completedSteps={completedSteps}
        onSelectStep={setStep}
      />

      {/* 3. Dynamic Step Content View */}
      {currentStep === 1 && <Step1SetupView />}

      {currentStep === 2 && (
        <Step2TitlesView onPushToEditor={onNavigateToEditor} />
      )}

      {currentStep === 3 && <Step3OutlineView />}

      {currentStep === 4 && (
        <Step4GenerationView onPushToEditor={onNavigateToEditor} />
      )}
    </div>
  );
};
